import { Button } from './Button'
import { StatusChip } from './StatusChip'
import type { AgreementStatus } from '../lib/download-flow'
import type { AcceptanceWithVersion, AgreementWithVersion, DocumentType } from '../lib/types'

export const docTypeLabels: Record<DocumentType, string> = {
  msa: 'Master Services Agreement',
  saas_tos: 'SaaS Terms of Service',
  eula: 'End User License Agreement',
  aup: 'Acceptable Use Policy',
  privacy: 'Privacy Policy',
  refund: 'Refund Policy',
  delivery_license: 'Delivery License',
  cookie: 'Cookie Policy',
}

export interface AgreementRowProps {
  agreement: AgreementWithVersion
  status: AgreementStatus | null
  acceptedRow: AcceptanceWithVersion | null
  onReview: () => void
  onViewAccepted: (row: AcceptanceWithVersion) => void
}

/** One legal document row: title, version chip, status chip and its actions. */
export function AgreementRow({
  agreement,
  status,
  acceptedRow,
  onReview,
  onViewAccepted,
}: AgreementRowProps) {
  const current = agreement.document_versions[0]
  return (
    <li className="flex flex-wrap items-center justify-between gap-4 rounded-2xl border border-line bg-ground-2 px-6 py-5">
      <div className="flex min-w-0 flex-col gap-1">
        <div className="flex flex-wrap items-center gap-3">
          <p className="text-body font-medium text-ink">{agreement.title}</p>
          <span className="rounded-chip border border-line bg-ground-3 px-3 py-0.5 text-small text-ink-muted">
            v{current?.version ?? '—'}
          </span>
          {status && (
            <StatusChip
              tone={status.chip === 'accepted' ? 'ready' : 'awaiting'}
              label={status.chip === 'accepted' ? 'Accepted' : 'Action required'}
            />
          )}
        </div>
        <p className="text-small text-ink-muted">
          {docTypeLabels[agreement.doc_type]}
          {current ? ` · effective ${new Date(current.effective_at).toLocaleDateString()}` : ''}
        </p>
        {status?.chip === 'accepted' && status.acceptedAt && (
          <p className="text-small text-ink-faint">
            Accepted {new Date(status.acceptedAt).toLocaleString()}
          </p>
        )}
        {status?.stale && status.acceptedAt && (
          <p className="text-small text-status-awaiting-fg">
            You accepted an earlier version on {new Date(status.acceptedAt).toLocaleDateString()} —
            this document has been updated.
          </p>
        )}
      </div>
      <div className="flex flex-wrap items-center gap-3">
        {status?.chip === 'accepted' && acceptedRow && (
          <Button variant="secondary" size="sm" onClick={() => onViewAccepted(acceptedRow)}>
            View accepted version
          </Button>
        )}
        {status?.chip !== 'accepted' && current && (
          <Button variant="primary" size="sm" onClick={onReview}>
            Review &amp; Accept
          </Button>
        )}
      </div>
    </li>
  )
}
